<template>
  <div>
    <EmailRecipientsDialog
      v-if="showRecipientsDialog"
      v-model="showRecipientsDialog"
      :portcallId="portcallId"
      :location="location"
      :client-id="clientId"
      :vessel-id="vesselId"
      :proforma-id="proformaId"
      @recipients-selected="recipientsSelected"
      @cancel="recipientsCancelled"
      :redirect="false"
      :to="emailTo"
      :cc="emailCc"
      :bcc="emailBcc" />
    <email-status
      v-for="generation in generations"
      :key="generation.guid"
      :showForm="showEmailStatusDialog && emailTemplateGuid === generation.guid"
      :guid="generation.guid"
      :managedGeneration="generation.managed"
      @close="close()"
      @documentReady="documentReady(generation)"
      @goToEmailClicked="goToEmailClicked($event)"
      @documentCreated="documentCreated($event, generation)"
      :noAction="noAction" />
  </div>
</template>

<script>
import { apiGet, apiDelete } from '@/store/api';
import { usePortcallStore } from '@/store/portcallStore';
import { isValidEmail } from '@/modules/email/emailFunctions';
import EmailStatus from '@/app-components/EmailStatus.vue';
import EmailRecipientsDialog from '@/modules/email/EmailRecipientsDialog.vue';
import { useApplicationStore } from '@/store/applicationStore';
import { getMailAttachments } from '@/services/api/api-calls/getMailAttachments';
import { generateDocument } from '@/services/api/api-calls/generateDocument';
import { getPendingEmailGenerations } from '@/services/api/api-calls/getPendingEmailGenerations';
import { getEmailGenerationStatus } from '@/services/api/api-calls/getEmailGenerationStatus';
import { getEmailTemplateRecipients } from '@/services/api/api-calls/getEmailTemplateRecipients';
import { GlobalHelperFunctions } from '@/common/GlobalHelperFunctions';
import SednaApiService from '@/services/api/external/sednaAPI';
import getLocationDetail from '../services/helpers/getLocationDetail';

export default {
  name: 'EmailGenerator',
  props: {
    location: String,
    portcallId: [String, Number],
    agencyIdOverride: [String, Number],
    locationIdOverride: [String, Number],
    vesselId: {
      type: [Number, String],
      required: false,
    },
    proformaId: {
      type: [Number, String],
      required: false,
    },
    clientId: {
      type: [Number, String],
      required: false,
    },
    guid: {
      type: String,
      default: undefined,
    },
    // If no action is needed from the user.
    noAction: {
      type: Boolean,
      default: false,
    },
    recoverGenerations: Boolean,
  },
  components: { EmailStatus, EmailRecipientsDialog },
  setup() {
    const applicationStore = useApplicationStore();
    const portcallStore = usePortcallStore();
    return {
      applicationStore,
      portcallStore,
    };
  },
  data() {
    return {
      showEmailStatusDialog: false,
      showRecipientsDialog: false,
      generations: [],
      recoveryRequest: undefined,
      isActive: true,
      emailTemplateGuid: undefined,
      emailParams: undefined,
      pendingTemplateId: undefined,
      pendingAppStatus: undefined,
      recipientDefaultsRequest: undefined,
      selectedRecipients: undefined,
      emailTo: [],
      emailCc: [],
      emailBcc: [],
    };
  },

  activated() {
    this.isActive = true;
  },

  deactivated() {
    this.isActive = false;
  },

  watch: {
    recoverGenerations(value) {
      this.recoveryRequest = value ? this.recoverEmailGenerations() : undefined;
      return this.recoveryRequest;
    },
    guid: {
      immediate: true,
      handler(val) {
        this.emailTemplateGuid = val;
        this.showEmailStatusDialog = val !== undefined;
        if (val) this.trackGeneration(val, false);
      },
    },
    locationId: {
      immediate: true,
      handler() {
        this.recoveryRequest = this.recoverGenerations ? this.recoverEmailGenerations() : undefined;
        return this.recoveryRequest;
      },
    },
  },

  computed: {
    askForRecipientsOnNewEmail() {
      return this.applicationStore.globalSettings.SGL_MAIL_ASK_ADDRESS === 1;
    },
    agencyId() {
      if (this.agencyIdOverride) return this.agencyIdOverride;
      if (this.portcallId && this.location !== 'proforma') {
        return this.portcallStore.callDetails.SETUP_ID;
      }
      return this.applicationStore.agencyIdSelected;
    },
    locationId() {
      if (this.locationIdOverride != null) return this.locationIdOverride;
      let locId;
      switch (this.location) {
        case 'client':
          locId = this.clientId;
          break;
        case 'vessel':
          locId = this.vesselId;
          break;
        case 'proforma':
          locId = this.proformaId;
          break;
        default:
          locId = this.portcallId;
      }
      return locId;
    },
    isLoading: {
      get() {
        return false;
      },
      set(val) {
        this.$emit('isLoading', val);
      },
    },
    isLoadingOverlay: {
      get() {
        return false;
      },
      set(val) {
        this.$emit('isLoadingOverlay', val);
      },
    },
  },

  methods: {
    async generateEmail(template, applicationStatus) {
      if (!this.locationId) return;
      this.recipientsCancelled();
      this.showEmailStatusDialog = false;
      this.pendingTemplateId = template.ID;
      this.pendingAppStatus = applicationStatus ? JSON.stringify(applicationStatus) : undefined;
      const askForRecipients = template.ASK_TITLE == null ? this.askForRecipientsOnNewEmail : template.ASK_TITLE === 1;
      if (askForRecipients) {
        let recipients = template.RECIPIENTS;
        if (template.HAS_ROLE_RECIPIENTS && this.portcallId) {
          const request = { location: this.location, locationId: this.locationId, portcallId: this.portcallId };
          this.recipientDefaultsRequest = request;
          this.isLoadingOverlay = true;
          try {
            recipients = await getEmailTemplateRecipients(
              request.location,
              this.agencyId,
              template.ID,
              request.portcallId
            );
            if (this.recipientDefaultsRequest !== request) return;
            if (
              request.location !== this.location ||
              request.locationId !== this.locationId ||
              request.portcallId !== this.portcallId
            ) {
              this.recipientsCancelled();
              return;
            }
          } catch (error) {
            if (this.recipientDefaultsRequest !== request) return;
            this.recipientsCancelled();
            throw error;
          } finally {
            if (this.recipientDefaultsRequest === request) {
              this.recipientDefaultsRequest = undefined;
              this.isLoadingOverlay = false;
            }
          }
        }
        this.emailTo = recipients?.to || [];
        this.emailCc = recipients?.cc || [];
        this.emailBcc = recipients?.bcc || [];
        this.showRecipientsDialog = true;
      } else {
        await this.generateTemplate();
      }
    },
    async generateTemplate() {
      const templateId = this.pendingTemplateId;
      if (templateId == null) return;
      this.pendingTemplateId = undefined;
      const appStatus = this.pendingAppStatus;
      this.pendingAppStatus = undefined;
      const recipients = this.selectedRecipients;
      this.selectedRecipients = undefined;
      this.isLoadingOverlay = true;
      try {
        const guid = GlobalHelperFunctions.GUID();
        const proformaId = this.location === 'portcall' ? this.proformaId : undefined;
        const emailGeneration = appStatus ? { recipients, appStatus } : { recipients };
        const result = await generateDocument(
          this.location,
          this.locationId,
          templateId,
          guid,
          1,
          proformaId,
          emailGeneration
        );
        if (result.resultCategory === 0 && result.resultType === 0) {
          this.trackGeneration(guid, true);
          this.emailTemplateGuid = guid;
          this.showEmailStatusDialog = true;
        } else {
          this.selectedRecipients = undefined;
        }
      } catch (error) {
        this.selectedRecipients = undefined;
        throw error;
      } finally {
        this.isLoadingOverlay = false;
      }
    },
    trackGeneration(guid, managed, documentId) {
      const generation = this.generations.find((job) => job.guid === guid);
      if (generation) {
        if (documentId) generation.documentId = documentId;
      } else {
        this.generations.push({ guid, managed, documentId, ready: false });
      }
    },
    async recoverEmailGenerations({ showStatus = true } = {}) {
      const { location, locationId } = this;
      if (!this.applicationStore.userRight('EM.EDIT')) return;
      if (!locationId || !['portcall', 'client', 'vessel', 'proforma'].includes(location)) return;
      const jobs = await getPendingEmailGenerations(location, locationId);
      if (location !== this.location || locationId !== this.locationId) return;
      jobs.forEach((job) => this.trackGeneration(job.DQU_ID, true, job.DQU_DOCUMENT_ID));
      if (showStatus && jobs.length && !this.showEmailStatusDialog && this.isActive) {
        this.emailTemplateGuid = jobs[jobs.length - 1].DQU_ID;
        this.showEmailStatusDialog = true;
      }
    },
    async finalizeEmailDraft(emailId) {
      await this.recoveryRequest;
      if (this.generations.some((job) => job.managed && !job.ready && !job.documentId)) {
        await this.recoverEmailGenerations({ showStatus: false });
      }
      const generation = this.generations.find((job) => job.managed && job.documentId === emailId && !job.ready);
      if (generation) {
        const status = await getEmailGenerationStatus(generation.guid);
        if (status.DQU_QUEUE_STATUS !== 3) throw new Error('The email is still being generated.');
        generation.ready = true;
      }
    },
    async createRecipientsArray(recipientsArray) {
      const error = [];
      const result = [];
      const promises = [];
      if (Array.isArray(recipientsArray) && recipientsArray.length > 0) {
        recipientsArray.forEach((item) => {
          if (isValidEmail(item.email)) {
            promises.push(
              apiGet(`portcall/emailRecipients/addManual/${item.email}`).then((res) => {
                if (Array.isArray(res) && res.length > 0) {
                  result.push({ ...res[0], EMAIL: res[0].EMAIL || item.email });
                } else {
                  result.push({ CLIENT_NAME: '', EMAIL: item.email, NAME: '' });
                }
              })
            );
          } else {
            error.push(`email ${item.email} is not a valid e-mail address`);
          }
        });
      }
      await Promise.allSettled(promises);
      return { error, result };
    },
    documentCreated(event, generation) {
      if (generation) {
        Object.assign(generation, { documentId: event.ID, ready: true });
      }
      this.$emit('documentCreated', event);
      if (generation?.managed) this.$emit('refreshEmail', null);
    },
    documentReady(generation) {
      if (!this.showEmailStatusDialog) {
        this.emailTemplateGuid = generation.guid;
        this.showEmailStatusDialog = true;
      }
    },
    close() {
      this.showEmailStatusDialog = false;
    },
    async goToEmailClicked(emailId, { isExistingDraft = false } = {}) {
      if (this.$listeners.goToEmailClicked) {
        this.$emit('goToEmailClicked', emailId);
        return;
      }
      const sednaApi = new SednaApiService(this.agencyId);
      let popup = sednaApi.isSednaIntegration && sednaApi.isCompactMode ? sednaApi.reserveComposerPopup() : undefined;
      try {
        if (popup?.blocked) return;
        if (isExistingDraft) await this.finalizeEmailDraft(+emailId);
        const email = await this.getEmail(emailId);
        if (email && typeof email.MAIL_HTML === 'string') {
          const managedGeneration = this.generations.some(
            (generation) => generation.managed && generation.documentId === +emailId
          );
          const selectedRecipients = isExistingDraft ? undefined : this.selectedRecipients;
          const toResult = selectedRecipients
            ? { result: selectedRecipients.to, error: [] }
            : await this.createRecipientsArray(email.TO);
          const ccResult = selectedRecipients
            ? { result: selectedRecipients.cc, error: [] }
            : await this.createRecipientsArray(email.CC);
          const bccResult = selectedRecipients
            ? { result: selectedRecipients.bcc, error: [] }
            : await this.createRecipientsArray(email.BCC);
          const to = toResult.result;
          const cc = ccResult.result;
          const bcc = bccResult.result;
          const onLoadError = [...toResult.error, ...ccResult.error, ...bccResult.error];
          const content = email.MAIL_HTML;
          const subject = email.SUBJECT;
          const mailNo = email.MAIL_NO;
          const attachments = email.MAIL_ATTACHMENTS;
          const { location } = this;
          const params = {
            emailId,
            location,
            content,
            mailNo,
            subject,
            to,
            cc,
            bcc,
            attachments,
            onLoadError,
            isDraft: true,
            createdBy: email.SENT_BY,
            templateFrom: email.EMAIL_FROM,
            templateFromValue: email.EMAIL_FROM_VALUE,
            sednaCategoryId: email.DLA_SEDNA_CATEGORY_ID,
            saveDraftOnLoad: !!selectedRecipients,
          };
          const askForRecipients = email.ASK_TITLE == null ? this.askForRecipientsOnNewEmail : email.ASK_TITLE === 1;
          if (!isExistingDraft && !managedGeneration && !selectedRecipients && askForRecipients) {
            this.emailParams = params;
            this.emailTo = to;
            this.emailCc = cc;
            this.emailBcc = bcc;
            this.showRecipientsDialog = true;
          } else {
            const composerPopup = popup;
            popup = undefined;
            await this.goToEmail(params, composerPopup);
            if (!isExistingDraft) this.selectedRecipients = undefined;
          }
        }
      } finally {
        popup?.close();
      }
    },
    async getEmail(emailId) {
      let result;
      try {
        result = await apiGet(`mail/${+emailId}`);
      } catch (error) {
        this.isLoadingOverlay = false;
        this.isLoading = false;
        console.error(error);
      }
      return result;
    },
    async getSednaJobIdForLocation() {
      const locationDetail = await getLocationDetail(this.locationId, this.location);
      return locationDetail?.SEDNA_JOB_ID;
    },
    async goToEmail(params = {}, composerPopup = undefined) {
      this.isLoadingOverlay = true;
      const sednaApi = new SednaApiService(this.agencyId);
      let popup = composerPopup;
      try {
        if (sednaApi.isSednaIntegration) {
          if (!popup && sednaApi.isCompactMode) popup = sednaApi.reserveComposerPopup();
          if (popup?.blocked) return;
          const attachmentsFromDocuments = params.emailId
            ? await getMailAttachments(params.emailId, params.attachments)
            : [];
          let sednaJobId = await this.getSednaJobIdForLocation();
          if (!sednaJobId) {
            sednaJobId = await sednaApi.createSednaJobRef(this.locationId, this.location);
          }
          if (sednaJobId) {
            this.portcallStore.updateSednaJobId({ id: this.locationId, sednaJobId });
            const composeEmailResponse = await sednaApi.composeNewSednaEmail(
              sednaJobId,
              attachmentsFromDocuments,
              params
            );
            if (this.openSednaEmail(sednaApi, composeEmailResponse, popup) === false) return;
            popup = undefined;
            if (params.emailId) {
              await apiDelete(`mail/${params.emailId}`);
            }
            this.$emit('refreshEmail', null);
          } else {
            throw new Error('The Sedna job reference could not be created.');
          }
        } else if (this.location === 'client' && this.locationId) {
          this.$router.push({
            name: 'clientEmailNew',
            params: {
              ...params,
              clientId: this.locationId,
            },
          });
        } else if (this.location === 'proforma' && this.locationId) {
          this.$router.push({
            name: 'proformaEmailNew',
            params: {
              ...params,
              proformaId: this.locationId,
            },
          });
        } else if (
          (this.location === 'portcall' ||
            this.location === 'expense' ||
            this.location === 'service' ||
            this.location === 'sof' ||
            this.location === 'visitor' ||
            this.location === 'crewchange crew' ||
            this.location === 'proforma' ||
            this.location === 'crewchange task') &&
          this.locationId
        ) {
          this.$router.push({
            name: 'prortcallEmailNew',
            params: {
              ...params,
              callId: this.locationId,
            },
          });
        } else if (this.location === 'vessel' && this.locationId) {
          this.$router.push({
            name: 'vesselEmailNew',
            params: {
              ...params,
              vesselId: this.locationId,
            },
          });
        }
      } finally {
        popup?.close();
        this.isLoadingOverlay = false;
      }
    },

    openSednaEmail(sednaApi, response, popup) {
      if (!response?.url || response.error) {
        throw new Error(response?.error || 'Sedna did not return a ready draft.');
      }
      return sednaApi.openNewSednaEmailUrl(response, popup, (result) => {
        if (result === 'sent' || result === 'discarded') this.$emit('refreshEmail', null);
      });
    },

    recipientsCancelled() {
      if (this.recipientDefaultsRequest) {
        this.recipientDefaultsRequest = undefined;
        this.isLoadingOverlay = false;
      }
      this.showRecipientsDialog = false;
      this.emailParams = undefined;
      this.pendingTemplateId = undefined;
      this.pendingAppStatus = undefined;
      this.selectedRecipients = undefined;
      this.emailTo = [];
      this.emailCc = [];
      this.emailBcc = [];
    },

    async recipientsSelected({ to, cc, bcc }) {
      this.showRecipientsDialog = false;
      if (this.pendingTemplateId != null) {
        this.selectedRecipients = { to, cc, bcc };
        await this.generateTemplate();
        return;
      }
      if (this.emailParams) {
        const params = {
          ...this.emailParams,
          to,
          cc,
          bcc,
          saveDraftOnLoad: true,
        };
        this.emailParams = undefined;
        this.emailTo = [];
        this.emailCc = [];
        this.emailBcc = [];
        await this.goToEmail(params);
      }
    },
  },
};
</script>

<style scoped></style>
